import type {CSSProperties, ReactElement} from 'react';
import React from 'react';
import type {UserAudioSource, UserAudioVOResponse} from '@Libraries/user-audio-library';
import {getAddedByTitle} from '@Components/base-grid/base-grid.library';
import {updateGridItem} from '@Components/base-grid';
import {AudioItem} from '@Components/base-grid/components/audio-item/audio-item';
import {GeneratedVoiceItem} from '@Panels/ai-voice-panel/components/generated-voice-item';
import {getGeneratedVoiceItemProps} from '@Panels/ai-voice-panel/components/generated-voice-item/generated-voice-item.library';
import type {GeneratedVoiceTileData} from '@Panels/ai-voice-panel/components/generated-voice-item/generated-voice-item.types';
import type {GridItemProps} from '../grid-item';
import {GRID_ITEM_TYPE, GridItem} from '../grid-item';
import type {GridMediaItemStorage} from '../grid-media-item';
import {GridMediaItem} from '../grid-media-item';
import {useAppDispatch} from '@/hooks';

export interface GridUserAudioItemStorage extends GridMediaItemStorage {
  type: GRID_ITEM_TYPE.USER_AUDIO;
  hashedFilename: string;
  uploaderName: string;
  name: string;
  uploaderId: number;
  duration: number;
  source: UserAudioSource;
  gridItemWidth?: number;
  isHidden?: boolean;
  idVoice?: number;
  generatedOn?: number;
}

interface GridUserAudioItemProps extends GridItemProps {
  hashedFilename: string;
  audioSrc: string;
  imgthumbSrc: string;
  name: string;
  uploaderName: string;
  duration: number;
  gridItemWidth?: number;
  isHidden?: boolean;
  idVoice?: number;
  generatedOn?: number;
}

export const getGridUserAudioItemStorageForUserAudioVO = (userAudioVO: UserAudioVOResponse): GridUserAudioItemStorage => {
  return {
    type: GRID_ITEM_TYPE.USER_AUDIO,
    id: userAudioVO.filename,
    hashedFilename: userAudioVO.filename,
    uploaderName: userAudioVO.uploaderName,
    uploaderId: parseInt(userAudioVO.uploaderId, 10),
    source: userAudioVO.src,
    name: userAudioVO.name,
    duration: userAudioVO.duration,
    isHidden: false,
    idVoice: userAudioVO.idVoice,
    generatedOn: userAudioVO.generatedOn,
  };
};

export function GridUserAudioItem({uploaderName, idVoice, generatedOn, ...props}: GridUserAudioItemProps): ReactElement | null {
  const dispatch = useAppDispatch();

  const onItemClicked = (e: React.MouseEvent, isDoubleClick: boolean): void => {
    if ($(e.target).hasClass('rename-audio-option') || $(e.target).parents().hasClass('rename-audio-option')) {
      return;
    }
    if (props.onClicked) {
      props.onClicked(e, isDoubleClick);
    }
  };

  const onNameChanged = async (newName: string): Promise<void> => {
    await window.PMW.writeLocal('posteraudio/updateName', {
      name: newName,
      hash: props.hashedFilename,
    });
    dispatch(
      updateGridItem({
        gridId: props.gridId ?? '',
        itemId: props.id,
        changes: {
          name: newName,
        },
      })
    );
  };

  if (props.isHidden) {
    return null;
  }

  const inlineStyles: CSSProperties = props.style ?? {};
  if (props.gridItemWidth) {
    inlineStyles.width = props.gridItemWidth;
  }

  const title = getAddedByTitle(uploaderName);

  const getAudioThumb = (): ReactElement<any> => {
    return (
      <AudioItem
        uid={props.id}
        gridId={props.gridId}
        title={props.name}
        duration={props.duration}
        source={props.audioSrc}
        showThreeDots
        onTitleRenamed={async (newTitle: string): Promise<void> => {
          await onNameChanged(newTitle);
        }}
      />
    );
  };

  const getGeneratedVoiceTile = (generatedVoice: GeneratedVoiceTileData): ReactElement => {
    return (
      <GridItem {...props} style={inlineStyles} title={title}>
        <GeneratedVoiceItem {...getGeneratedVoiceItemProps(generatedVoice, props.audioSrc)} shouldShowDateOnSeparateRow />
      </GridItem>
    );
  };

  if (idVoice !== undefined && generatedOn !== undefined) {
    return getGeneratedVoiceTile({...props, idVoice, generatedOn});
  }
  return <GridMediaItem {...props} onClicked={onItemClicked} style={inlineStyles} className={props.className ?? ''} title={title} paddedThumb={false} thumb={getAudioThumb()} />;
}
